// 主人公の姿：初めから選べる10種＋月パスの10種（一度買えば永続）。TOP・メニュー・チャットで同じ保存値を使う。
// チャットのアイコン縁（月パスの期間中だけ）もここで選ぶ。期限が切れたら縁なしに戻る（core の shownAvatar / shownFrame）
import { CHAT_FRAMES, chatFrame, ENEMIES, DEFAULT_PLAYER_AVATAR, hasPremiumAvatars, isChatFrame, isPlayerAvatar, isPremiumAvatar, monthlyActive, PLAYER_AVATARS, playerAvatar, shownAvatar, shownFrame, type Profile } from '@hyakki/core';
import { api } from '../api';
import { enemyIconHtml } from '../assets';
import { act, state } from '../state';
import { esc } from './dom';
import { openModal, toast } from './overlay';
import { errorText } from './status';
import { rem } from './viewport';
import '../styles/avatar.css';

const SILHOUETTE = `<svg viewBox="0 0 40 40" aria-hidden="true"><circle cx="20" cy="15" r="7.5"/><path d="M5.5 37c1.2-8.4 7.2-13 14.5-13s13.3 4.6 14.5 13z"/></svg>`;
export const myAvatar = (p: Profile | null = state.profile) => (p ? shownAvatar(p, Date.now()) : null);
export const myFrame = (p: Profile | null = state.profile) => (p ? shownFrame(p, Date.now()) : null);
const member = () => !!state.profile && monthlyActive(state.profile, Date.now());
/** 月パスの姿（一度買えば期限が切れても使える） */
const avatarOwner = () => !!state.profile && hasPremiumAvatars(state.profile);
const frameUrl = (file: string) => `/art/chat-frames/${file}`;
// 公開するのは幅400の WebP（原本は art-src/player-avatar-concepts の目視確認済み PNG。1枚1.3MB あって名前登録の画面で読み込みが遅かった）
const imageUrl = (file: string) => `/art/player-avatars/${file}?v=webp-20261023`;
/** 姿の絵すべて（名前登録の前に先読みする） */
export const avatarImageUrls = () => PLAYER_AVATARS.map((a) => imageUrl(a.file));

/** 小さな表示は顔〜上半身。過去の妖怪アイコン・未設定の発言も表示可能。 */
export function avatarHtml(id: string | null | undefined, size = 40, cls = '', frame?: string | null): string {
  const player = playerAvatar(id);
  const enemy = id && Object.hasOwn(ENEMIES, id) ? ENEMIES[id] : undefined;
  const inner = player ? `<img src="${imageUrl(player.file)}" alt="" decoding="async">` : enemy ? enemyIconHtml(id!, size) : SILHOUETTE;
  const av = `<span class="avatar ${player ? 'player-avatar' : enemy ? '' : 'none'} ${frame ? '' : cls}" style="width:${rem(size)};height:${rem(size)}" role="img" aria-label="${esc(player?.name ?? enemy?.name ?? 'アイコン')}">${inner}</span>`;
  const f = chatFrame(frame);
  // 縁の絵は穴の中心＝絵の中心、穴の半径＝アイコンの半径になる大きさ（art-src/chat-frames/normalize.py）
  return f ? `<span class="av-framed ${cls}" style="width:${rem(size)};height:${rem(size)}">${av}<img class="av-ring" src="${frameUrl(f.file)}" alt="" decoding="async"></span>` : av;
}

/** 名前登録と変更画面で共用する、キーボードでも選べる全身の一覧。 */
export function avatarChoicesHtml(current: string | null = DEFAULT_PLAYER_AVATAR): string {
  const selected = isPlayerAvatar(current) && (!isPremiumAvatar(current) || avatarOwner()) ? current : DEFAULT_PLAYER_AVATAR;
  // 名前登録の画面では無料の10種だけ。変更の画面では月パスの10種も（期間外なら鍵）
  const list = state.me?.user.displayName ? PLAYER_AVATARS : PLAYER_AVATARS.filter(a => !isPremiumAvatar(a.id));
  return `<fieldset class="av-choices"><legend>あなたの姿</legend>
    <p class="av-hint">好きな姿を選んでください。あとから無料で変更できます。</p>
    <div class="av-options">${list.map(a => {
      const locked = isPremiumAvatar(a.id) && !avatarOwner();
      return `<label class="av-option ${isPremiumAvatar(a.id) ? 'premium' : ''}">
      <input type="radio" name="avatar" value="${a.id}" ${a.id === selected ? 'checked' : ''} ${locked ? 'disabled' : ''} required>
      <span class="av-card"><img src="${imageUrl(a.file)}" alt="" decoding="async"><span>${a.name}</span><i aria-hidden="true">✓</i>${isPremiumAvatar(a.id) ? `<em class="av-paid">${locked ? '🔒' : ''}月パス</em>` : ''}</span>
    </label>`;
    }).join('')}</div></fieldset>`;
}

/** チャットのアイコン縁（月パス）。期間外なら鍵 */
function frameChoicesHtml(current: string | null, avatar: string | null): string {
  const locked = !member();
  const opt = (id: string, label: string, ring: string) => `<label class="av-option fr-option">
      <input type="radio" name="frame" value="${id}" ${id === (current ?? '') ? 'checked' : ''} ${locked && id ? 'disabled' : ''}>
      <span class="av-card">${ring}<span>${label}</span><i aria-hidden="true">✓</i></span>
    </label>`;
  return `<fieldset class="av-choices"><legend>アイコンの縁${locked ? '<em class="av-paid">🔒月パス</em>' : ''}</legend>
    <div class="av-options">${opt('', 'なし', avatarHtml(avatar, 44))}${CHAT_FRAMES.map(f => opt(f.id, f.name, avatarHtml(avatar, 44, '', f.id))).join('')}</div></fieldset>`;
}

export function openAvatarPicker(onDone?: () => void) {
  if (!state.profile) return;
  const cur = state.profile.settings?.avatar ?? null;
  const curFrame = state.profile.settings?.frame ?? null;
  // 姿と縁はタブで切り替え（1画面に収める）
  const m = openModal(`<form class="av-form"><div class="tabs seg-tabs av-tabs" role="tablist"><button type="button" class="tab-btn on" role="tab" aria-selected="true" data-tab="av">姿</button><button type="button" class="tab-btn" role="tab" aria-selected="false" data-tab="fr">アイコンの縁</button></div>
    <div data-pane="av">${avatarChoicesHtml(cur)}</div><div data-pane="fr" hidden>${frameChoicesHtml(myFrame(), myAvatar() ?? DEFAULT_PLAYER_AVATAR)}</div><button type="submit" class="btn primary wide">決定</button></form>`,
    { title: '姿を変える', className: 'sheet-wrap navy-sheet av-sheet' });
  const form = m.body.querySelector<HTMLFormElement>('form')!;
  form.querySelectorAll<HTMLButtonElement>('[data-tab]').forEach(b => b.addEventListener('click', () => {
    form.querySelectorAll<HTMLButtonElement>('[data-tab]').forEach(x => {
      x.classList.toggle('on', x === b);
      x.setAttribute('aria-selected', String(x === b));
    });
    form.querySelectorAll<HTMLElement>('[data-pane]').forEach(p => p.hidden = p.dataset.pane !== b.dataset.tab);
  }));
  // 縁の見本は選んでいる姿で
  form.addEventListener('change', e => {
    const t = e.target as HTMLInputElement;
    if (t.name !== 'avatar') return;
    const pane = form.querySelector<HTMLElement>('[data-pane="fr"]')!;
    const fr = new FormData(form).get('frame');
    pane.innerHTML = frameChoicesHtml(isChatFrame(fr) ? fr : null, t.value);
  });
  let busy = false;
  form.addEventListener('submit', async e => {
    e.preventDefault();
    if (busy) return;
    const data = new FormData(form);
    const id = data.get('avatar');
    const fr = data.get('frame');
    const frame = isChatFrame(fr) ? fr : null;
    if (!isPlayerAvatar(id)) return;
    const frameChanged = member() && frame !== curFrame;
    if (id === cur && !frameChanged) return m.close();
    busy = true;
    const controls = form.querySelectorAll<HTMLInputElement | HTMLButtonElement>('input, button');
    controls.forEach(b => b.disabled = true);
    try {
      if (id !== cur) await act(api.setAvatar(id));
      if (frameChanged) await act(api.setFrame(frame));
      m.close();
      onDone?.();
      toast('変更しました。', 'good');
    } catch (err) {
      toast(errorText(err), 'error');
    } finally {
      busy = false;
      controls.forEach(b => b.disabled = false);
    }
  });
}
